import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import '../styles/Dashboard.css';

interface Recording {
  id: string;
  call_id: string;
  session_id: string;
  format: string;
  duration_seconds: number;
  size_bytes: number;
  customer_name?: string;
  customer_phone?: string;
  transcript?: string;
  recorded_at: string;
  created_at: string;
  hasAudio?: boolean;
  vartotojo_id?: string;
  ai_summary?: {
    trumpa_santrauka?: string;
    kliento_poreikis?: string;
    pasiulyti_sprendimai?: string[];
    aptartos_paslaugos?: string[];
    kaina_saskaita?: string;
    sutarti_veiksmai?: string[];
    sekantis_zingsnis?: string;
    pokalbio_nuotaika?: string;
    svarbus_klausimas?: string;
    kontakto_informacija?: {
      vardas?: string;
      telefonas?: string;
      adresas?: string;
    };
    darbu_detales?: {
      darbo_tipas?: string;
      apimtis?: string;
      terminai?: string;
    };
    has_important_info?: boolean;
  };
}

const RecordingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin/');
  const [recording, setRecording] = useState<Recording | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchRecording();

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [id]);

  const fetchRecording = async () => {
    try {
      setLoading(true);

      let endpoint: string;

      if (isAdminRoute) {
        // For admin routes, check admin authentication
        const authRes = await fetch('/api/auth/status', { credentials: 'include' });
        const authData = await authRes.json();

        if (!authData.authenticated || !authData.user?.isAdmin) {
          navigate('/admin/login');
          return;
        }

        endpoint = `/api/admin/recordings/${id}`;
      } else {
        // For regular user routes
        endpoint = `/api/user/recordings/${id}`;
      }

      // Fetch recording details
      const response = await fetch(endpoint, {
        credentials: 'include'
      });

      console.log('Recording API Response Status:', response.status);
      console.log('Response Headers:', response.headers);

      if (response.status === 401) {
        // Save the current URL to redirect back after login
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        navigate(isAdminRoute ? '/admin/login' : '/prisijungti');
        return;
      }

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Nepavyko gauti įrašo duomenų';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Response is not JSON (likely HTML)
        }
        throw new Error(errorMessage);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Netinkamas serverio atsakymas. Galbūt reikia prisijungti?');
      }

      const data = await response.json();
      console.log('Recording API Response Data:', data);

      // Handle response format - both admin and user now use the same format
      let recordingData = null;
      let audioBase64Data = null;

      if (data.success && data.recording) {
        recordingData = data.recording;
        audioBase64Data = data.recording.audio_base64;
      } else if (data.recording) {
        recordingData = data.recording;
        audioBase64Data = data.recording.audio_base64;
      }

      if (recordingData) {
        setRecording(recordingData);
        console.log('Recording set:', recordingData);

        // Check if audio is included in the response
        if (audioBase64Data) {
          // Create audio URL from base64 data
          const binaryString = window.atob(audioBase64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: recordingData.format || 'audio/wav' });
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);

          if (audioRef.current) {
            audioRef.current.src = url;
          }
        } else {
          // Load audio separately if not included
          await loadAudio();
        }
      } else {
        throw new Error('Įrašas nerastas');
      }
    } catch (error) {
      console.error('Error fetching recording:', error);
      setError(error instanceof Error ? error.message : 'Klaida kraunant įrašą');
    } finally {
      setLoading(false);
    }
  };

  const loadAudio = async () => {
    try {
      const endpoint = isAdminRoute
        ? `/api/admin/recordings/${id}/audio`
        : `/api/user/recordings/${id}/audio`;

      const response = await fetch(endpoint, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_data) {
          // Create audio blob from base64 data
          const binaryString = window.atob(data.audio_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const mimeType = data.format === 'mp3' ? 'audio/mp3' : 'audio/webm';
          const blob = new Blob([bytes], { type: mimeType });
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
        }
      }
    } catch (error) {
      console.error('Error loading audio:', error);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        if (updateIntervalRef.current) {
          clearInterval(updateIntervalRef.current);
        }
      } else {
        audioRef.current.play();
        updateIntervalRef.current = setInterval(() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
          }
        }, 100);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTranscript = (transcript: string) => {
    // Parse transcript if it's a conversation format
    const lines = transcript.split('\n').filter(line => line.trim()); // Filter out empty lines
    return lines.map((line, index) => {
      // Match any speaker format: "Name: message" or "DI: message"
      const speakerMatch = line.match(/^([^:]+):\s*(.*)/);
      if (speakerMatch) {
        const [, speaker, message] = speakerMatch;
        const speakerLower = speaker.toLowerCase().trim();
        const isAssistant = speakerLower === 'di' || speakerLower === 'ai' || speakerLower === 'assistant';

        // Skip empty messages
        if (!message || !message.trim()) {
          return null;
        }

        return (
          <div key={index} style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            background: isAssistant
              ? '#f9f9f9'
              : 'white',
            borderLeft: `3px solid ${isAssistant
              ? '#FFC107'
              : '#003366'}`,
            borderRadius: '4px'
          }}>
            <strong style={{
              color: isAssistant
                ? '#FFC107'
                : '#003366',
              marginRight: '0.5rem'
            }}>
              {speaker}:
            </strong>
            <span style={{ color: '#666' }}>{message}</span>
          </div>
        );
      }
      return <p key={index} style={{ marginBottom: '0.5rem' }}>{line}</p>;
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '3px solid #e0e0e0',
            borderTopColor: '#FFC107',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#666' }}>Kraunamas įrašas...</p>
        </div>
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}> Klaida</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            {error || 'Įrašas nerastas'}
          </p>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'white',
              color: '#003366',
              border: '1px solid #003366',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Grįžti atgal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{ color: '#003366', margin: 0 }}>
             Skambučio įrašas
          </h1>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'white',
              color: '#003366',
              border: '1px solid #003366',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            ← Grįžti
          </button>
        </div>

        {/* Recording Info Card */}
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          marginBottom: '2rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem'
          }}>
            <div>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Sesijos ID</span>
              <p style={{ margin: '0.25rem 0', fontWeight: '600', color: '#003366' }}>
                #{recording.session_id ? recording.session_id.slice(-8) : recording.id.slice(-8)}
              </p>
            </div>
            <div>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Data ir laikas</span>
              <p style={{ margin: '0.25rem 0', fontWeight: '600', color: '#003366' }}>
                {new Date(recording.recorded_at).toLocaleString('lt-LT')}
              </p>
            </div>
            <div>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Trukmė</span>
              <p style={{ margin: '0.25rem 0', fontWeight: '600', color: '#003366' }}>
                {formatTime(recording.duration_seconds)}
              </p>
            </div>
            <div>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Dydis</span>
              <p style={{ margin: '0.25rem 0', fontWeight: '600', color: '#003366' }}>
                {(recording.size_bytes / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            {recording.customer_name && (
              <div>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>Klientas</span>
                <p style={{ margin: '0.25rem 0', fontWeight: '600', color: '#003366' }}>
                  {recording.customer_name}
                </p>
              </div>
            )}
            {recording.customer_phone && (
              <div>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>Telefonas</span>
                <p style={{ margin: '0.25rem 0', fontWeight: '600', color: '#003366' }}>
                  {recording.customer_phone}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* AI Summary */}
        {recording.ai_summary && (
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            marginBottom: '2rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              color: '#003366',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
               AI Santrauka
              {recording.ai_summary.has_important_info && (
                <span style={{
                  background: '#dc3545',
                  color: 'white',
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  marginLeft: '0.5rem'
                }}>
                  SVARBU
                </span>
              )}
            </h3>

            {/* Main Summary */}
            {recording.ai_summary.trumpa_santrauka && (
              <div style={{
                background: '#f9f9f9',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0, color: '#333' }}>
                  {recording.ai_summary.trumpa_santrauka}
                </p>
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem'
            }}>
              {/* Client Need */}
              {recording.ai_summary.kliento_poreikis && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Kliento poreikis
                  </h4>
                  <p style={{ margin: 0, color: '#333' }}>
                    {recording.ai_summary.kliento_poreikis}
                  </p>
                </div>
              )}

              {/* Proposed Solutions */}
              {recording.ai_summary.pasiulyti_sprendimai && recording.ai_summary.pasiulyti_sprendimai.length > 0 && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Pasiūlyti sprendimai
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    {recording.ai_summary.pasiulyti_sprendimai.map((item, idx) => (
                      <li key={idx} style={{ color: '#333' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Services Discussed */}
              {recording.ai_summary.aptartos_paslaugos && recording.ai_summary.aptartos_paslaugos.length > 0 && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Aptartos paslaugos
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    {recording.ai_summary.aptartos_paslaugos.map((item, idx) => (
                      <li key={idx} style={{ color: '#333' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Price/Invoice */}
              {recording.ai_summary.kaina_saskaita && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Kaina / Sąskaita
                  </h4>
                  <div style={{
                    background: '#fff3cd',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #ffc107'
                  }}>
                    <p style={{ margin: 0, color: '#856404' }}>
                      {recording.ai_summary.kaina_saskaita}
                    </p>
                  </div>
                </div>
              )}

              {/* Agreed Actions */}
              {recording.ai_summary.sutarti_veiksmai && recording.ai_summary.sutarti_veiksmai.length > 0 && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Sutarti veiksmai
                  </h4>
                  <div style={{
                    background: '#d4edda',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #28a745'
                  }}>
                    <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                      {recording.ai_summary.sutarti_veiksmai.map((item, idx) => (
                        <li key={idx} style={{ color: '#155724' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Next Step */}
              {recording.ai_summary.sekantis_zingsnis && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Sekantis žingsnis
                  </h4>
                  <div style={{
                    background: '#cce5ff',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #004085'
                  }}>
                    <p style={{ margin: 0, color: '#004085' }}>
                      {recording.ai_summary.sekantis_zingsnis}
                    </p>
                  </div>
                </div>
              )}

              {/* Work Details */}
              {recording.ai_summary.darbu_detales &&
               (recording.ai_summary.darbu_detales.darbo_tipas ||
                recording.ai_summary.darbu_detales.apimtis ||
                recording.ai_summary.darbu_detales.terminai) && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Darbų detalės
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    {recording.ai_summary.darbu_detales.darbo_tipas && (
                      <tr>
                        <td style={{ padding: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>
                          Tipas:
                        </td>
                        <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: '#333' }}>
                          {recording.ai_summary.darbu_detales.darbo_tipas}
                        </td>
                      </tr>
                    )}
                    {recording.ai_summary.darbu_detales.apimtis && (
                      <tr>
                        <td style={{ padding: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>
                          Apimtis:
                        </td>
                        <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: '#333' }}>
                          {recording.ai_summary.darbu_detales.apimtis}
                        </td>
                      </tr>
                    )}
                    {recording.ai_summary.darbu_detales.terminai && (
                      <tr>
                        <td style={{ padding: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>
                          Terminai:
                        </td>
                        <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: '#333' }}>
                          {recording.ai_summary.darbu_detales.terminai}
                        </td>
                      </tr>
                    )}
                  </table>
                </div>
              )}

              {/* Contact Info */}
              {recording.ai_summary.kontakto_informacija &&
               (recording.ai_summary.kontakto_informacija.vardas ||
                recording.ai_summary.kontakto_informacija.telefonas ||
                recording.ai_summary.kontakto_informacija.adresas) && (
                <div>
                  <h4 style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                     Kontaktinė informacija
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    {recording.ai_summary.kontakto_informacija.vardas && (
                      <tr>
                        <td style={{ padding: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>
                          Vardas:
                        </td>
                        <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: '#333' }}>
                          {recording.ai_summary.kontakto_informacija.vardas}
                        </td>
                      </tr>
                    )}
                    {recording.ai_summary.kontakto_informacija.telefonas && (
                      <tr>
                        <td style={{ padding: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>
                          Telefonas:
                        </td>
                        <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: '#333' }}>
                          {recording.ai_summary.kontakto_informacija.telefonas}
                        </td>
                      </tr>
                    )}
                    {recording.ai_summary.kontakto_informacija.adresas && (
                      <tr>
                        <td style={{ padding: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>
                          Adresas:
                        </td>
                        <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: '#333' }}>
                          {recording.ai_summary.kontakto_informacija.adresas}
                        </td>
                      </tr>
                    )}
                  </table>
                </div>
              )}
            </div>

            {/* Mood and Important Questions */}
            <div style={{
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {recording.ai_summary.pokalbio_nuotaika && (
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Pokalbio nuotaika: <strong>{recording.ai_summary.pokalbio_nuotaika}</strong>
                </div>
              )}
              {recording.ai_summary.svarbus_klausimas && (
                <div style={{
                  fontSize: '0.85rem',
                  color: '#dc3545',
                  fontWeight: 'bold'
                }}>
                   {recording.ai_summary.svarbus_klausimas}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Audio Player */}
        {audioUrl && (
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            marginBottom: '2rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#003366', marginBottom: '1rem' }}>
               Garso atkūrimas
            </h3>

            <audio
              ref={audioRef}
              src={audioUrl}
              onLoadedMetadata={(e) => {
                const audio = e.target as HTMLAudioElement;
                setDuration(audio.duration);
              }}
              onEnded={() => {
                setIsPlaying(false);
                if (updateIntervalRef.current) {
                  clearInterval(updateIntervalRef.current);
                }
              }}
            />

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <button
                onClick={handlePlayPause}
                style={{
                  minWidth: '100px',
                  padding: '0.75rem 1.5rem',
                  background: '#FFC107',
                  color: '#003366',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {isPlaying ? 'Pauzė' : 'Atkurti'}
              </button>

              <div style={{ flex: 1 }}>
                <input
                  type="range"
                  min="0"
                  max={duration}
                  value={currentTime}
                  onChange={handleSeek}
                  style={{
                    width: '100%',
                    accentColor: '#FFC107'
                  }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0.25rem',
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>
                Greitis:
              </span>
              {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map((speed) => (
                <button
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    background: playbackSpeed === speed ? '#FFC107' : '#f5f5f5',
                    color: playbackSpeed === speed ? 'white' : '#666',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Transcript */}
        {recording.transcript && (
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <h3 style={{ color: '#003366', marginBottom: '1rem' }}>
               Pokalbio transkriptas
            </h3>
            <div style={{
              maxHeight: '600px',
              overflowY: 'auto',
              padding: '1rem',
              background: '#f9f9f9',
              borderRadius: '4px'
            }}>
              {formatTranscript(recording.transcript)}
            </div>
          </div>
        )}

        {!recording.transcript && (
          <div style={{
            background: '#f9f9f9',
            padding: '3rem',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e0e0e0'
          }}>
            <p style={{ color: '#666' }}>
               Transkriptas neprieinamas
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingDetail;